import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import './App.css';
import { NavigationProvider } from './contexts/NavigationContext';
import { SessionProvider } from './contexts/SessionContext';
import { ConnectionStateProvider } from './contexts/ConnectionStateContext';
import ScreenRouter from './components/Navigation/ScreenRouter';
import { getProfileData, ensureProfileSynced } from './services/storage/userDataStorage';
import { checkAndRecoverData } from './services/storage/dataRecovery';
import { mergeMyNumberAtOpen } from './services/auth/numberMerge';
import {
  validateDevice,
  subscribeToInvalidation,
  handleInvalidation,
} from './services/session/deviceSession';
import { extractChatRoomId } from './utils/chatUtils';
import { isValidProfileUserId } from './utils/identityUtils';
import { ensureAnonIdentity } from './services/auth/anonIdentity';
import { claimQrRoom } from './services/activeSession';
import { isFullTier, isProfileWorld, isSimpleTier } from './utils/userTier';
import { setPendingProfileScan } from './utils/pendingProfileScan';
import { hasDownstreamLadder } from './utils/exchangeVariant';
import { initInstallPromptCapture } from './utils/installPrompt';
import { hideInstantSplash } from './utils/instantSplash';
import { logEvent } from './services/analytics/eventLogger';
// contactQueueService removed — sendMessage is now DB-first
import { updateFocusState } from './services/pushNotificationService';
import { DemoGate } from './components/Auth/DemoGate';
import { warmupRealtimeConnection, authReady, getSignInFailure } from './services/supabase';
import { startVersionCheck } from './services/versionCheck';

// PRE-WARM: Start Supabase realtime connection AFTER auth is established.
// Auth must complete first so the WebSocket carries the JWT.
// authReady resolves near-instantly for returning users (localStorage session).
authReady.then(() => warmupRealtimeConnection());

// PERFORMANCE: Synchronous check BEFORE React renders anything
// This determines if we should show Hello immediately
const isAnonQRScan = (() => {
  const params = new URLSearchParams(window.location.search);
  const shortScreen = params.get('s');
  const directScreen = params.get('screen');
  const hasChatRoomId =
    params.get('c') || params.get('chatRoomId') || window.location.search.includes('chat_');

  // It's a QR scan if: has anon screen param AND has chatRoomId
  return (shortScreen === 'a' || directScreen === 'anon-exchange-flow') && hasChatRoomId;
})();

// Check for permanent URL route: /u/:profileUserId
// This is for the new connection mechanism with permanent QR codes
const permanentUrlMatch = (() => {
  const pathname = window.location.pathname;
  const match = pathname.match(/^\/u\/([a-zA-Z0-9_-]+)$/);
  if (match) {
    return { profileUserId: match[1] };
  }
  return null;
})();

// Profile User Screens
// QRScreen is no longer a routed screen — it's rendered by QROverlay (see ScreenRouter)
import ProfileScreen from './screens/ProfileUser/ProfileScreen';
import ChatListPage from './screens/ProfileUser/ChatListPage';
import PostsFeedPage from './screens/ProfileUser/PostsFeedPage';

// Anonymous User Screen
import AnonConnectionPage from './components/Connection/AnonConnectionPage';
import AnonConnectionsListPage from './components/Connection/AnonConnectionsListPage';
import AnonExchangeFlow from './screens/AnonUser/AnonExchangeFlow';
import AnonChatListPage from './screens/AnonUser/AnonChatListPage';
// Connection Pages
import ProfileConnectionPage from './components/Connection/ProfileConnectionPage';
import ProfileConnectionDetailPage from './components/Connection/ProfileConnectionDetailPage';
import ProfileConnectionsListPage from './components/Connection/ProfileConnectionsListPage';
import AnonConnectionDetailPage from './components/Connection/AnonConnectionDetailPage';
import ManualAddDetailPage from './components/Connection/ManualAddDetailPage';

// UI Showcase Screen
import UISystemScreen from './screens/UIShowcase/UISystemScreen';
// /new demo — its own chunk: main (flag locked off) never downloads it.
const NewExperienceDemo = lazy(() => import('./screens/NewExperience/NewExperienceDemoScreen'));
const NewExperienceDemoScreen = (props) => (
  <Suspense fallback={null}>
    <NewExperienceDemo {...props} />
  </Suspense>
);
// /new/people — the new People screen (SIGNUP Plan 3), its own chunk too.
const NewPeople = lazy(() => import('./screens/NewExperience/PeopleScreen'));
const NewPeopleScreen = (props) => (
  <Suspense fallback={null}>
    <NewPeople {...props} />
  </Suspense>
);
// /new/guest?frame=… — the guest exchange's fixture gallery (signup E3a), its own chunk.
const NewGuestGallery = lazy(
  () => import('./screens/NewExperience/GuestExchange/GuestExchangeGallery')
);
const NewGuestGalleryScreen = (props) => (
  <Suspense fallback={null}>
    <NewGuestGallery {...props} />
  </Suspense>
);
// new-profile — your own Profile page (signup D1), its own chunk too.
const NewProfile = lazy(() => import('./screens/NewExperience/ProfilePage'));
const NewProfileScreen = (props) => (
  <Suspense fallback={null}>
    <NewProfile {...props} />
  </Suspense>
);
// new-detail — the new Detail page (signup D1), its own chunk too. Once People
// has fetched it, render it directly: a lazy boundary waits ~300 ms even for a
// chunk already in hand, which would stall the §9a opening (detailLoader.js).
const NewDetail = lazy(loadDetailScreen);
const NewDetailScreen = (props) => {
  const Loaded = loadedDetailScreen();
  return Loaded ? (
    <Loaded {...props} />
  ) : (
    <Suspense fallback={null}>
      <NewDetail {...props} />
    </Suspense>
  );
};
import { isNewExperience } from './utils/newExperience';
import { loadDetailScreen, loadedDetailScreen } from './screens/NewExperience/detailLoader';

// Permanent URL Redirect Screen
import UserRedirect from './screens/UserRedirect';

// Signup Page
import SignupPage from './screens/SignupPage';
import ProfileSignupSuccessScreen from './screens/ProfileSignupSuccessScreen';

// Welcome Page (dead-end for unknown users)
import WelcomePage from './screens/WelcomePage';

// Utility Pages
import ClearStoragePage from './screens/ClearStoragePage';

// Admin Pages
import AnalyticsDashboard from './screens/Admin/AnalyticsDashboard';
import ErrorLog from './screens/Admin/ErrorLog';

// Easter Egg Pages
import TomDetailPage from './screens/TomDetailPage';
import TomBackgroundShowcase from './screens/TomBackgroundShowcase';

// Session Handoff
import { useConversionCheck } from './hooks/useConversionCheck';
import useLiveLocationRefresh from './hooks/useLiveLocationRefresh';
import ConvertedTakeoverScreen from './components/ConvertedTakeoverScreen';
import CantConnectScreen from './components/Auth/CantConnectScreen';
import { consumeDevToolsParam } from './utils/devTools';

function App() {
  // Session handoff: detect if Anon was converted in PWA
  const { converted, variant, clearMarker } = useConversionCheck();

  // Sign-in gave up after its own retries → the can't-connect screen in place
  // of the whole app (signup-cant-connect): nothing can load without it
  const [signInFailure, setSignInFailure] = useState(null);
  useEffect(() => {
    authReady.then(() => setSignInFailure(getSignInFailure()));
  }, []);

  // State for user type detection
  const [userType, setUserType] = useState('anon'); // 'profile' or 'anon'
  const [chatRoomId, setChatRoomId] = useState(null);
  // The host's name when their code is mid-exchange with someone else (busy screen)
  const [busyName, setBusyName] = useState(null);
  const [initialScreen, setInitialScreen] = useState('loading');
  // QR overlay: starts open for Profile users (covers list on load, no flash)
  const [initialQROpen, setInitialQROpen] = useState(false);

  // PERFORMANCE: Hide the instant splash from index.html when React is ready.
  // The exchange flow owns this hide moment — connectionTiming measures the
  // spinner duration from it. Normal opens are hidden by ScreenRouter instead.
  const handleAnonReady = useCallback(() => {
    console.log('App: AnonExchangeFlow signaled ready, hiding instant splash');
    hideInstantSplash();
  }, []);

  // CALLBACK: UserRedirect found a session — route in-app instead of redirecting
  // Only has chatRoomId (no profile name/photo), so AnonExchangeFlow takes normal WebSocket path
  const handleSessionFound = useCallback((foundChatRoomId) => {
    console.log('App: UserRedirect found session, routing in-app:', foundChatRoomId);

    // Guests only: a profile holder opening a link never reaches UserRedirect —
    // their Connect screen scans it instead (signup-connect step 3).
    // No profile data available — serverRendered: false means AnonExchangeFlow
    // waits for WebSocket. The win is eliminating the double page load.
    window.__EXCHANGE_DATA__ = {
      chatRoomId: foundChatRoomId,
      profileUserId: permanentUrlMatch?.profileUserId,
      serverRendered: false,
      dataSource: 'redirect',
    };

    setChatRoomId(foundChatRoomId);
    setInitialScreen('anon-exchange-flow');
  }, []);

  // Reset document title
  useEffect(() => {
    document.title = 'Royale';
  }, []);

  // Capture the PWA install prompt app-wide so the conversion flow's Android
  // install button can fire it without a page reload (see installPrompt.js).
  useEffect(() => {
    initInstallPromptCapture();
  }, []);

  // Warm-reopen version check — ALL users now (app-shell-instant-open.md). The
  // cached app shell means an Anon reopen no longer guarantees fresh code, so
  // Anon needs the check too; the mid-exchange gate (window.__EXCHANGE_DATA__)
  // keeps it from ever reloading during a scan. Idempotent.
  useEffect(() => {
    startVersionCheck();
  }, []);

  // Contact queue removed — sendMessage is now DB-first

  // Migrate existing profiles to Supabase + run data recovery if needed
  useEffect(() => {
    console.log('App: Profile sync useEffect triggered, userType:', userType);
    if (userType === 'profile') {
      // Skip sync during onboarding — profile data doesn't exist yet,
      // so recovery would find "missing" data and trigger an infinite reload loop
      if (localStorage.getItem('needs_onboarding') === 'true') {
        console.log('App: Skipping profile sync — onboarding in progress');
        return;
      }
      console.log('App: userType is profile, waiting for auth then calling ensureProfileSynced...');
      authReady
        .then(() => ensureProfileSynced())
        .then(async (synced) => {
          console.log('App: ensureProfileSynced completed, result:', synced);
          // In parallel: the merge on the number (Full only, server-checked —
          // Simple profiles on this number hand over their connections,
          // f/simple-merge) and the data recovery check (localStorage loss)
          const [restored, recovered] = await Promise.all([
            mergeMyNumberAtOpen(),
            checkAndRecoverData(),
          ]);
          return recovered || restored > 0;
        })
        .then((recovered) => {
          if (recovered) {
            console.log('App: Data recovery completed — reloading to pick up restored data');
            window.location.reload();
          }
        })
        .catch((err) => console.warn('App: Profile sync/recovery failed:', err));
    }
  }, [userType]);

  // Device session enforcement — subscribe to invalidation + validate on mount/wake
  // Full-only: single-device enforcement is an OTP-login concept; a Simple
  // profile has no login to enforce.
  useEffect(() => {
    if (userType !== 'profile' || !isFullTier()) return;
    const profileUserId = localStorage.getItem('profile_user_id');
    const deviceId = localStorage.getItem('device_id');
    if (!profileUserId || !deviceId) return;

    // Path 3: Validate on mount (after auth is ready)
    authReady
      .then(() => validateDevice(profileUserId))
      .then((isValid) => {
        if (!isValid) {
          console.log('App: Device session invalid on mount');
          handleInvalidation('mount_check');
        }
      });

    // Path 1: Subscribe to broadcast
    const unsubscribe = subscribeToInvalidation(profileUserId, handleInvalidation);

    // Path 2: Validate on visibility change (tab wake)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validateDevice(profileUserId).then((isValid) => {
          if (!isValid) {
            console.log('App: Device session invalid on wake');
            handleInvalidation('visibility_check');
          }
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userType]);

  // Live location — keep a sharer's neighborhood fresh for peers (app open +
  // every foreground). Reconciles the sharing flag from the DB at open.
  useLiveLocationRefresh(userType);

  // Push notification suppression — write focus state to DB for server-side check
  // Server-side suppression is primary (works on all platforms including iOS).
  // SW clients.matchAll() is desktop backup only.
  useEffect(() => {
    // Full-only: push notifications are a Full feature.
    if (userType !== 'profile' || !isFullTier()) return;

    authReady.then(() => updateFocusState(true));

    const handlePushVisibility = () => {
      updateFocusState(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handlePushVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handlePushVisibility);
    };
  }, [userType]);

  // Detect user type and chat room ID from URL on initial load
  useEffect(() => {
    // FIRST, before any early return: ?dev=1 / ?dev=0 sets the dev-chrome flag
    // and strips itself from the URL. It runs ahead of every fast path on
    // purpose — the param can ride in on any entry (a room link, /signup), and
    // a fast path that returned first would leave it in the address bar to be
    // copied and shared. Sets storage only; nothing renders differently here.
    consumeDevToolsParam();

    // ACCOUNT RESET: /reset shows confirmation then wipes all data (server + local)
    if (window.location.pathname === '/reset') {
      setInitialScreen('clear-storage');
      return; // Don't continue init — ClearStoragePage handles confirmation + reset
    }

    // FAST PATH: Check for /signup page - public entry point for new users
    const isSignupPage = window.location.pathname === '/signup';
    if (isSignupPage) {
      console.log('App: FAST PATH - Signup page detected');
      setInitialScreen('signup');
      return; // Skip all other initialization
    }

    // FAST PATH: Check for /clear page - utility for clearing storage during testing
    const isClearPage = window.location.pathname === '/clear';
    if (isClearPage) {
      console.log('App: FAST PATH - Clear storage page detected');
      setInitialScreen('clear-storage');
      return; // Skip all other initialization
    }

    // FAST PATH: Check for /ui page - UI showcase
    if (window.location.pathname === '/ui') {
      setInitialScreen('ui-showcase');
      return;
    }

    // FAST PATH: /new — SIGNUP foundations demo (branch deploys only; on main
    // the flag is locked off and /new falls through to the normal app)
    if (window.location.pathname === '/new' && isNewExperience()) {
      setInitialScreen('new-experience-demo');
      return;
    }

    // /new/people?rung=… — the People fixture gallery needs no account
    if (
      window.location.pathname === '/new/people' &&
      isNewExperience() &&
      new URLSearchParams(window.location.search).has('rung')
    ) {
      setInitialScreen('new-people');
      return;
    }

    // /new/guest?frame=… — the guest exchange's fixture gallery needs no account
    if (
      window.location.pathname === '/new/guest' &&
      isNewExperience() &&
      new URLSearchParams(window.location.search).has('frame')
    ) {
      setInitialScreen('new-guest-gallery');
      return;
    }

    // /new/detail?pairing=… — the Detail fixture gallery needs no account
    if (
      window.location.pathname === '/new/detail' &&
      isNewExperience() &&
      new URLSearchParams(window.location.search).has('pairing')
    ) {
      setInitialScreen('new-detail');
      return;
    }

    // FAST PATH: Check for /admin/dashboard page - analytics dashboard
    const isAdminDashboard = window.location.pathname === '/admin/dashboard';
    if (isAdminDashboard) {
      console.log('App: FAST PATH - Admin dashboard detected');
      setInitialScreen('admin-dashboard');
      return; // Skip all other initialization
    }

    // FAST PATH: Check for /admin/errors page - error log viewer
    const isAdminErrors = window.location.pathname === '/admin/errors';
    if (isAdminErrors) {
      console.log('App: FAST PATH - Admin errors detected');
      setInitialScreen('admin-errors');
      return; // Skip all other initialization
    }

    // REFRESH DETECTION: If Anon already has an active session in this tab,
    // this is a page refresh — NOT a new QR scan.
    // Route directly to connection page with existing chatRoomId.
    // Avoids re-running the full exchange flow (markAnonConnected, confirmGuestConnection, etc.)
    //
    // NOTE: Only possible false positive is if someone manually types a different /u/ URL
    // in the same tab. No in-app code path navigates to /u/ within an existing tab.
    const existingSessionChatRoomId = sessionStorage.getItem('sessionChatRoomId');
    const existingUserType = sessionStorage.getItem('user_type');

    if (existingSessionChatRoomId && existingUserType === 'anon' && permanentUrlMatch) {
      console.log('App: REFRESH DETECTED - Anon has existing session, routing to connection page');
      sessionStorage.setItem('user_type', 'anon');
      setUserType('anon');
      setChatRoomId(existingSessionChatRoomId);
      // Route back into the exchange flow whenever it — not the chat page —
      // owns the Anon screen. Sans-chat (D6): People is home, so the flow
      // shows the lo-fi card to a virgin returner and redirects a participated
      // one to People. THREAD (plan 3): the flow owns the screen so its
      // interstitial can gate, and the flow then puts a participated returner
      // back in the thread rather than on People — the thread is the artifact.
      // (Formation effects re-run idempotently: the virgin-check keeps origin
      // metadata, and the confirmed_at columns are first-wins.)
      // The guest id is claimed before the chat mounts (f/identity-ownership):
      // instant when this session already owns it — the usual refresh — and a
      // replaced session rotates to a fresh id first (ensureAnonIdentity).
      let cancelledRefresh = false;
      ensureAnonIdentity().then(() => {
        if (cancelledRefresh) return;
        setInitialScreen(hasDownstreamLadder() ? 'anon-exchange-flow' : 'anon-connection-page');
      });
      return () => {
        cancelledRefresh = true;
      };
    }

    // PERMANENT URL (/u/<id>) — who is opening it?
    if (permanentUrlMatch) {
      const existingProfileId = localStorage.getItem('profile_user_id');
      const existingProfileData = localStorage.getItem('profile_user_data');
      if (existingProfileId && !isValidProfileUserId(existingProfileId)) {
        console.warn('App: Invalid profile_user_id format, clearing:', existingProfileId);
        localStorage.removeItem('profile_user_id');
        logEvent('identity_invalid_format', {
          found: existingProfileId,
          had_profile_data: Boolean(existingProfileData),
          path: window.__PREFETCH__ ? 'prefetch' : 'fallback',
        });
      }

      // PROFILE HOLDER (Simple or Full — has-a-profile, not Full tier: the
      // signup-epic PXP ruling). Opening a link is a scan, so it runs the one
      // profile-to-profile handshake: their own Connect screen opens over
      // People and scans the link once ready (pendingProfileScan → QRScreen →
      // handlePXPGuestScan, the door the in-app scanner and Proximity use).
      // The old route dropped the scanner straight into the host's fresh room
      // with no handshake — a blank room and a host stuck on their QR (§2).
      // Asked after the malformed-id clear above (isProfileWorld reads fresh).
      if (isProfileWorld()) {
        console.log('App: Profile holder opened a link — scanning it from their Connect screen');
        setPendingProfileScan(window.location.href);
        window.history.replaceState({}, '', '/');
        sessionStorage.setItem('user_type', 'profile');
        sessionStorage.setItem('nav_qr_open', 'true');
        setUserType('profile');
        setInitialScreen('profile-connections-list');
        setInitialQROpen(true);
        return;
      }
    }

    // PREFETCH PATH: a guest (no profile) opens a permanent URL with inline prefetch data.
    // index.html started direct Supabase calls in parallel with JS bundle download.
    // Resolve the promise, populate __EXCHANGE_DATA__, route to exchange flow.
    // If prefetch failed (null), fall through to UserRedirect.
    if (permanentUrlMatch && window.__PREFETCH__) {
      console.log('App: PREFETCH PATH - Permanent URL with prefetch, awaiting data');
      let cancelled = false;

      // Wait for prefetch data, the auth session AND the guest-id claim
      // before routing. Prefetch fetches profile data; auth ensures JWT is
      // ready for DB writes; the claim binds the guest id to this session
      // before anything can publish it (f/identity-ownership). All run in
      // parallel — we just gate on all completing.
      // The claim needs only the room and the session, so it runs alongside
      // the guest-id claim rather than after it
      const claimed = Promise.all([window.__PREFETCH__, authReady]).then(([data]) =>
        data?.chatRoomId ? claimQrRoom(data.chatRoomId) : null
      );
      Promise.all([window.__PREFETCH__, authReady, ensureAnonIdentity(), claimed]).then(
        ([data, , , claim]) => {
          if (cancelled) return;

          sessionStorage.setItem('user_type', 'anon');
          setUserType('anon');
          // One exchange per code at a time (052): the live room is claimed
          // (above) before anything is written into it. Someone else
          // mid-exchange → the busy screen; anything else (code closed, the call
          // failed) → UserRedirect, which looks again and shows Not Available or
          // the error screen.
          if (claim === 'busy') {
            console.log('App: Code is mid-exchange with someone else — busy screen');
            setBusyName(data.name);
            setInitialScreen('user-redirect');
            return;
          }
          if (claim === 'claimed') {
            console.log('App: Prefetch resolved with data:', data.chatRoomId);

            // Populate __EXCHANGE_DATA__ for AnonExchangeFlow to consume on mount
            // serverRendered: true — means "profile data available at mount, skip WebSocket wait"
            window.__EXCHANGE_DATA__ = {
              chatRoomId: data.chatRoomId,
              profileUserId: data.profileUserId,
              profileName: data.name,
              profilePhoto: data.photoUrl,
              // 050: the host's colour + photo crop, for the card's first frame
              profileColour: data.colour || null,
              profilePhotoMeta: data.photoMeta || null,
              serverRendered: true,
              dataSource: 'prefetch',
              prefetchMs: data.prefetchMs,
            };
            setChatRoomId(data.chatRoomId);
            setInitialScreen('anon-exchange-flow');
          } else {
            // Prefetch failed or no active session — route to UserRedirect
            console.log('App: Prefetch returned null, falling through to UserRedirect');
            setInitialScreen('user-redirect');
          }
        }
      );

      // Return cleanup: prevents setState on unmounted component + handles StrictMode
      // This is the useEffect's return value. Existing branches return undefined (early exit).
      return () => {
        cancelled = true;
      };
    }

    // FALLBACK: a guest opens a permanent URL without prefetch (shouldn't happen —
    // index.html always sets it; also covers /u/ visited without __PREFETCH__)
    if (permanentUrlMatch) {
      console.log(
        'App: FALLBACK - Permanent URL without prefetch:',
        permanentUrlMatch.profileUserId
      );
      // Set user_type early for DemoGate route protection
      sessionStorage.setItem('user_type', 'anon');
      setUserType('anon');
      setInitialScreen('user-redirect');
      // profileUserId will be passed via initialParams
      return; // Skip all other initialization
    }

    // ONBOARDING: Just completed pending profile conversion — route to profile setup
    // This runs after reload from hydrateFromAuth(). The user has a profile_user_id
    // but hasn't filled out their name/photo yet.
    if (localStorage.getItem('needs_onboarding') === 'true') {
      console.log('App: Pending conversion complete — routing to onboarding');
      sessionStorage.setItem('user_type', 'profile');
      setUserType('profile');
      setInitialScreen('profile-settings');
      return;
    }

    // NOTIFICATION TAP: App opened from push notification with chatRoomId in URL
    const notifyChatRoomId = new URLSearchParams(window.location.search).get('notify');
    // Full-only + tier-asked: push taps route into chat (a Full surface); the
    // old bare profile_user_id check was the weakest predicate in the codebase.
    if (notifyChatRoomId && isFullTier()) {
      console.log('App: NOTIFICATION TAP - routing to chat:', notifyChatRoomId);
      sessionStorage.setItem('user_type', 'profile');
      sessionStorage.setItem('sessionChatRoomId', notifyChatRoomId);
      setUserType('profile');
      setChatRoomId(notifyChatRoomId);
      setInitialScreen('profile-connection-page');
      // Clean up URL
      window.history.replaceState({}, '', '/');
      return;
    }

    // FAST PATH: New visitor (no profile, not mid-signup) → show signup welcome page
    // Users who clicked "Get Started" have signup_initiated flag, so they skip this.
    // NOTE: This MUST come after pathname route checks above (/u/, /clear, /admin, /signup)
    // because those routes should work regardless.
    const hasProfileId = Boolean(localStorage.getItem('profile_user_id'));
    const hasSignupInitiated = Boolean(sessionStorage.getItem('signup_initiated'));
    const hasScreenParam =
      new URLSearchParams(window.location.search).has('screen') ||
      new URLSearchParams(window.location.search).has('s');
    if (!hasProfileId && !hasSignupInitiated && !hasScreenParam) {
      console.log('App: FAST PATH - New visitor, showing welcome page');
      setInitialScreen('welcome');
      return;
    }

    // FAST PATH: For QR scans detected synchronously, set screen immediately
    // This is the CRITICAL optimization - no async operations on the hot path
    if (isAnonQRScan) {
      console.log('App: FAST PATH - QR scan detected, setting screen immediately');

      // Extract chatRoomId synchronously (needed for both anon and profile paths)
      const urlParams = new URLSearchParams(window.location.search);
      let chatRoomIdFromUrl = urlParams.get('chatRoomId') || urlParams.get('c');
      const search = window.location.search;
      if (!chatRoomIdFromUrl && search.startsWith('?') && search.includes('chat_')) {
        const potentialId = search.substring(1).split('&')[0];
        if (potentialId.startsWith('chat_')) {
          chatRoomIdFromUrl = potentialId;
        }
      }

      // Phase 0.5: Check if scanning user is already a Profile user
      // If so, route to PXP flow instead of Anon flow
      // Require both keys — profile_user_id alone may be stale from aborted conversion.
      const existingProfileId = localStorage.getItem('profile_user_id');
      const existingProfileData = localStorage.getItem('profile_user_data');
      if (existingProfileId && !isValidProfileUserId(existingProfileId)) {
        console.warn('App: Invalid profile_user_id format, clearing:', existingProfileId);
        localStorage.removeItem('profile_user_id');
        logEvent('identity_invalid_format', {
          found: existingProfileId,
          had_profile_data: Boolean(existingProfileData),
          path: 'fast',
        });
      }
      // PXP gates on FULL tier (SP-scan ruling) — tier asked, not re-derived
      if (isFullTier()) {
        console.log('App: Profile user detected (PXP), routing to profile-connection-page');
        sessionStorage.setItem('user_type', 'profile');
        setUserType('profile');
        if (chatRoomIdFromUrl) {
          setChatRoomId(chatRoomIdFromUrl);
        }
        setInitialScreen('profile-connection-page');
        return; // Skip anon flow
      }

      // Set user_type early for DemoGate route protection
      sessionStorage.setItem('user_type', 'anon');
      setUserType('anon');
      setInitialScreen('anon-exchange-flow');
      if (chatRoomIdFromUrl) {
        setChatRoomId(chatRoomIdFromUrl);
      }
      return; // Skip all other initialization
    }

    // SLOW PATH: Normal initialization for non-QR-scan cases
    // Parse URL parameters
    const url = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const isAnon = urlParams.get('anon') === 'true';

    // Extract room ID from URL if present
    const roomId = extractChatRoomId(url);
    if (roomId) {
      setChatRoomId(roomId);
    }

    // PERFORMANCE OPTIMIZATION: Check screen parameter first to avoid unnecessary async checks
    const directScreen = urlParams.get('screen');
    const shortScreen = urlParams.get('s');

    // Determine if this is clearly an anon user based on screen parameter
    const isAnonScreen =
      directScreen === 'anon-exchange-flow' ||
      shortScreen === 'a' || // shortened anon-exchange-flow
      directScreen === 'anon-connection-page' ||
      directScreen === 'anon-connection-detail' ||
      directScreen === 'anon-connections-list' ||
      directScreen === 'anon-chat';

    // Set user type based on URL parameter or profile data
    const storedUserType = sessionStorage.getItem('user_type');

    if (storedUserType === 'profile') {
      // User has already been identified as profile - respect their stored type
      console.log('App: Using stored user_type:', storedUserType);
      setUserType(storedUserType);
      setInitialScreenBasedOnURL(storedUserType);
    } else if (isAnon || isAnonScreen) {
      // Fresh QR scans and explicit anon screens - skip profile check for faster load
      setUserType('anon');
      setInitialScreenBasedOnURL('anon');
    } else {
      // Check if user has profile data (only for ambiguous cases)
      const checkProfileData = async () => {
        const profileData = await getProfileData();
        if (profileData && profileData.name) {
          setUserType('profile');
          // Set user_type for DemoGate
          sessionStorage.setItem('user_type', 'profile');

          setInitialScreenBasedOnURL('profile'); // Pass userType
        } else {
          setUserType('anon');
          setInitialScreenBasedOnURL('anon'); // Pass userType
        }
      };

      checkProfileData();
    }
  }, []);

  // Simple function to determine initial screen using direct screen approach
  const setInitialScreenBasedOnURL = (currentUserType) => {
    const urlParams = new URLSearchParams(window.location.search);

    // Check for direct screen specification (support both old 'screen' and new shortened 's' parameter)
    const directScreen = urlParams.get('screen');
    const shortScreen = urlParams.get('s');

    // Support shortened screen parameter format
    let resolvedScreen = directScreen;
    if (!resolvedScreen && shortScreen) {
      // Map shortened screen codes to full screen names
      if (shortScreen === 'a') resolvedScreen = 'anon-exchange-flow';
    }

    // Profile users should not reach anon screens via URL params
    if (
      currentUserType === 'profile' &&
      resolvedScreen &&
      (resolvedScreen.startsWith('anon-') || shortScreen === 'a')
    ) {
      console.log('App: Profile user with anon URL params - redirecting to connections list');
      logEvent('nav_guard_blocked', {
        attempted: resolvedScreen,
        redirectedTo: 'profile-connections-list',
        userType: 'profile',
        source: 'app_init',
      });
      resolvedScreen = 'profile-connections-list';
    }

    // Anon users should not reach profile screens via URL params
    if (currentUserType === 'anon' && resolvedScreen && resolvedScreen.startsWith('profile-')) {
      console.log('App: Anon user with profile URL params - redirecting to anon-connections-list');
      logEvent('nav_guard_blocked', {
        attempted: resolvedScreen,
        redirectedTo: 'anon-connections-list',
        userType: 'anon',
        source: 'app_init',
      });
      resolvedScreen = 'anon-connections-list';
    }

    // Support multiple chatRoomId formats:
    // 1. Named parameter: ?chatRoomId=xxx or ?c=xxx
    // 2. Direct: /?chat_123456789_abcdef
    let chatRoomIdFromUrl = urlParams.get('chatRoomId') || urlParams.get('c');

    // Check for the simplest format: /?chat_123456789_abcdef
    const search = window.location.search;
    if (!chatRoomIdFromUrl && search.startsWith('?') && search.length > 1) {
      // Get everything after the ? character
      const potentialChatRoomId = search.substring(1);

      // Validate that it looks like a chatRoomId (starts with 'chat_')
      if (potentialChatRoomId.startsWith('chat_')) {
        chatRoomIdFromUrl = potentialChatRoomId;
        console.log('App: Extracted chatRoomId from simple query format:', chatRoomIdFromUrl);
      }
    }

    // CRITICAL: If we have a chatRoomId and no screen specified, we should go to anon-exchange-flow
    if (chatRoomIdFromUrl && !resolvedScreen && currentUserType === 'anon') {
      console.log(
        'App: ChatRoomId found in URL without screen parameter, defaulting to anon-exchange-flow'
      );
      resolvedScreen = 'anon-exchange-flow';
    }

    if (resolvedScreen) {
      console.log('App: Using screen specification:', resolvedScreen);
      setInitialScreen(resolvedScreen);

      // If chatRoomId is provided in URL, use it
      if (chatRoomIdFromUrl) {
        console.log('App: Using chatRoomId from URL:', chatRoomIdFromUrl);
        setChatRoomId(chatRoomIdFromUrl);
        // We'll establish this as the session chatRoomId in the SessionProvider
      }

      // Clean URL params after consuming them - they're a one-time entry signal.
      // Without this, browser refresh reloads stale params and routes to wrong screen.
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Default screen logic based on user type
    if (currentUserType === 'profile') {
      // /new/people — the new People screen on this account's real data
      // (branch deploys only; the flip swaps the landing itself)
      if (window.location.pathname === '/new/people' && isNewExperience()) {
        setInitialScreen('new-people');
        return;
      }
      // A Simple profile's home is the new People screen on branch deploys
      // (signup E3a F4): the guest who minted on the new design lands there,
      // and a refresh keeps them there. Full users' default is unchanged
      // (E3b/E3c); main never takes this branch (the flag is locked off).
      if (isNewExperience() && isSimpleTier()) {
        setInitialScreen('new-people');
        return;
      }
      // Check sessionStorage for reload persistence
      const savedScreen = sessionStorage.getItem('nav_current_screen');
      const validScreens = [
        'profile-connections-list',
        'profile-settings',
        'profile-signup-success',
      ];
      const restoredScreen = validScreens.includes(savedScreen)
        ? savedScreen
        : 'profile-connections-list';

      // Restore QR overlay state if it was open before reload
      const wasQROpen = sessionStorage.getItem('nav_qr_open') === 'true';

      console.log(`App: Profile user, screen: ${restoredScreen}, QR open: ${wasQROpen}`);
      setInitialScreen(restoredScreen);
      if (wasQROpen) {
        setInitialQROpen(true);
      }
    } else {
      // Authenticated users with no profile data should go to signup to create profile
      // This handles the case: user logged in before but profile_user_data is missing
      console.log('App: No screen specified and no profile data, redirecting to signup');
      setInitialScreen('signup');
    }
  };

  // CRITICAL: Create a stable wrapper component using useMemo
  // This prevents AnonExchangeFlow from being remounted when App re-renders
  // Without this, the inline arrow function creates a new component type on every render
  const AnonExchangeFlowWrapper = useMemo(() => {
    const Wrapper = (props) => <AnonExchangeFlow {...props} onReady={handleAnonReady} />;
    Wrapper.displayName = 'AnonExchangeFlowWrapper';
    return Wrapper;
  }, [handleAnonReady]);

  // Stable wrapper for UserRedirect — binds onSessionFound callback
  // Mirrors AnonExchangeFlowWrapper pattern to keep initialParams clean
  // busyName: set by the prefetch path when the code is mid-exchange with
  // someone else (navigation's params are fixed at mount, so it rides here);
  // a profile holder's scan passes its own through navigateTo.
  const UserRedirectWrapper = useMemo(() => {
    const Wrapper = (props) => (
      <UserRedirect
        {...props}
        busyName={props.busyName || busyName}
        onSessionFound={handleSessionFound}
      />
    );
    Wrapper.displayName = 'UserRedirectWrapper';
    return Wrapper;
  }, [handleSessionFound, busyName]);

  // Define screen components for the router
  const screenComponents = useMemo(
    () => ({
      // Profile user screens
      'profile-settings': ProfileScreen,
      'profile-connection-page': ProfileConnectionPage,
      'profile-connection-detail': ProfileConnectionDetailPage,
      'profile-connections-list': ProfileConnectionsListPage,
      'profile-chat-list': ChatListPage,
      'profile-posts': PostsFeedPage,
      'anon-connection-detail': AnonConnectionDetailPage,
      'manual-add-detail': ManualAddDetailPage,

      // Anonymous user screens
      'anon-connection-page': AnonConnectionPage,
      'anon-connections-list': AnonConnectionsListPage,
      'anon-chat-list': AnonChatListPage,
      'anon-exchange-flow': AnonExchangeFlowWrapper,
      // UI showcase
      'ui-showcase': UISystemScreen,
      'new-experience-demo': NewExperienceDemoScreen,
      'new-people': NewPeopleScreen,
      'new-detail': NewDetailScreen,
      'new-profile': NewProfileScreen,
      'new-guest-gallery': NewGuestGalleryScreen,

      // Permanent URL redirect (with in-app callback to avoid double page load)
      'user-redirect': UserRedirectWrapper,

      // Signup page (public, no auth required)
      signup: SignupPage,
      'profile-signup-success': ProfileSignupSuccessScreen,

      // Welcome page (dead-end for unknown users at / with no state)
      welcome: WelcomePage,

      // Utility pages
      'clear-storage': ClearStoragePage,

      // Admin pages
      'admin-dashboard': AnalyticsDashboard,
      'admin-errors': ErrorLog,

      // Easter egg pages
      'tom-detail': TomDetailPage,
      'tom-backgrounds': TomBackgroundShowcase,
    }),
    [AnonExchangeFlowWrapper, UserRedirectWrapper]
  );

  // PERFORMANCE: Memoize initial params to prevent new object reference on each render
  // Without this, every App re-render (Hello state changes) creates a new object,
  // which propagates through NavigationProvider and causes ScreenRouter to re-render
  const initialParams = useMemo(
    () => ({
      chatRoomId,
      userType,
      // Include profileUserId for permanent URL redirect
      profileUserId: permanentUrlMatch?.profileUserId || null,
    }),
    [chatRoomId, userType]
  );

  if (signInFailure) {
    return <CantConnectScreen cause={signInFailure} />;
  }

  // Session handoff: old tab was converted — show takeover screen
  if (converted) {
    return <ConvertedTakeoverScreen variant={variant} onClear={clearMarker} />;
  }

  return (
    <div className="App">
      {/* Hello is now rendered in index.html for instant display before JS loads */}
      {/* React hides it via handleAnonReady when AnonConnectionPage is ready */}

      <DemoGate>
        <SessionProvider>
          <ConnectionStateProvider>
            <NavigationProvider
              initialScreen={initialScreen}
              initialParams={initialParams}
              initialQROpen={initialQROpen}
            >
              <ScreenRouter screens={screenComponents} />
            </NavigationProvider>
          </ConnectionStateProvider>
        </SessionProvider>
      </DemoGate>
    </div>
  );
}

export default App;
